import { toaster } from "@/components/ui/toaster";
import { CheckRequestStatus } from "@/constants/entity-status";
import { AppRoutes } from "@/constants/routes";
import {
  createCheckRequest,
  CreateCheckRequestProps,
  updateCheckRequest,
} from "@/services/check-requests.service";
import { handlePermissionError } from "@/utils/permission-check";
import { formatCurrencyInputToNumber } from "@/utils/query-string";
import { AxiosError } from "axios";
import { Dispatch, SetStateAction } from "react";
import { NavigateFunction } from "react-router-dom";
import { CheckRequestDetail } from "../schema/check-requests.schema";
import { EstadosCheque, EstadosECheq } from "@/constants/enums/status/entity-status-ids";
import { RequestTypes } from "@/constants/enums";

interface CRHProps {
  setLoadingCreateCheckRequest: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  watchedValues: {
    person?: any;
    account?: any;
    sucursal?: any;
    request_amount?: any;
    request_detail?: any;
    usuarioMobile?: any;
    tipoSolicitud?: any;
    fechaVto?: any;
  };
  fetchPermissions: () => Promise<void>;
}

export const createRequestHandler = async ({
  navigate,
  setLoadingCreateCheckRequest,
  watchedValues,
  fetchPermissions,
}: CRHProps) => {
  setLoadingCreateCheckRequest(true);
  try {
    const objToSend: CreateCheckRequestProps = {
      nroCuentaOrigen: watchedValues?.account?.value,
      claveSucursal:
        watchedValues?.tipoSolicitud?.[0] === RequestTypes.ECheq
          ? "POS"
          : watchedValues?.sucursal?.[0],
      mensaje: watchedValues?.request_detail,
      monto: formatCurrencyInputToNumber(watchedValues?.request_amount ?? 0),
      idUsuarioMobile: watchedValues?.usuarioMobile?.value,
      fechaVto: watchedValues?.fechaVto,
      tipoSolicitud: watchedValues?.tipoSolicitud?.[0],
    };

    await createCheckRequest(objToSend);

    toaster.create({
      title: "Solicitud creada con éxito",
      type: "success",
    });
    setLoadingCreateCheckRequest(false);
    navigate(AppRoutes.CHECK_REQUESTS);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.CHECK_REQUESTS,
    );
    setLoadingCreateCheckRequest(false);

    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se ha podido crear la solicitud.",
        duration: errorObj?.response?.data?.message?.includes("política")
          ? 10000
          : 4000,
      });
      throw error;
    }
  }
};

export interface EditAmountHandlerProps {
  request?: CheckRequestDetail | null;
  errors: any;
  getValues: any;
  fetchRequest: any;
  setLoadingEditAmount: Dispatch<SetStateAction<boolean>>;
  setIsEditingAmount: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const editAmountHandler = async ({
  request,
  errors,
  getValues,
  fetchRequest,
  setLoadingEditAmount,
  setIsEditingAmount,
  navigate,
  fetchPermissions,
}: EditAmountHandlerProps) => {
  if (request?.estado !== CheckRequestStatus.NEEDS_INFO) return;
  if (errors?.request_amount) return;

  try {
    setLoadingEditAmount(true);

    await updateCheckRequest({
      idSolicitud: request?.id,
      estado: request?.idTipoSolicitud == RequestTypes.Cheque ? EstadosCheque.RequiereInformacion : EstadosECheq.RequiereInformacion,
      monto: formatCurrencyInputToNumber(getValues("request_amount")),
    });

    await fetchRequest(false);

    setLoadingEditAmount(false);
    setIsEditingAmount((prev) => !prev);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );

    if (handled) setIsEditingAmount(false);
    
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorObj?.message ?? "No se pudo editar el monto solicitado.",
      });
    }
  } finally {
    setLoadingEditAmount(false);
  }
};
